// 週期任務 (recurring task) rule helpers — shared by the create/edit dialog, the
// board card, and the detail drawer so the cadence logic can't drift apart.
//
// A recurring task is an ordinary card that lives in the 週期 column and STAYS
// there. It is a standing reminder, not a to-do: nobody has to press 完成. Its
// due date is an ANCHOR, and the next occurrence is derived from it on the fly
// (anchor + k × interval, first one that is not in the past). So a card set to
// 每3週 keeps showing its next date forever, with no cron and no bookkeeping.
//
// Rules the UI writes are always 'every:{n}d' / 'every:{n}w' — an interval and a
// unit, any n. Fixed-weekday forms ('daily', 'weekly:3', 'monthly:15') still
// parse for older rows; the UI maps them onto their nearest interval.

export type RecurUnit = '' | 'd' | 'w';

// The 重複 control is a unit dropdown plus a number: 不重複 / 每 N 天 / 每 N 週.
export const RECUR_UNITS: Array<{ value: RecurUnit; label: string }> = [
  { value: '', label: '不重複' },
  { value: 'd', label: '天' },
  { value: 'w', label: '週' },
];

// Largest interval the backend accepts per unit (server caps at 365 days).
export const maxForUnit = (unit: RecurUnit): number => (unit === 'w' ? 52 : 365);

const WEEKDAY_LABELS: Record<string, string> = {
  '1': '一', '2': '二', '3': '三', '4': '四', '5': '五', '6': '六', '7': '日',
};

// Render a rule as a zh-TW label: 'every:3w' → 每3週 · 'every:1w' → 每週.
// An unrecognized rule falls through as-is rather than rendering blank, so a
// future rule form stays visible instead of silently disappearing.
export const recurrenceLabel = (rule?: string | null): string => {
  if (!rule) return '';
  if (rule === 'daily') return '每天';
  if (rule.startsWith('weekly:')) {
    const n = rule.split(':')[1];
    return `每週${WEEKDAY_LABELS[n] ?? n}`;
  }
  if (rule.startsWith('monthly:')) {
    const n = rule.split(':')[1];
    return `每月${n}號`;
  }
  const m = /^every:(\d{1,3})([dw])(?:@|$)/.exec(rule);
  if (m) {
    const unitLabel = m[2] === 'w' ? '週' : '天';
    return m[1] === '1' ? `每${unitLabel}` : `每${m[1]}${unitLabel}`;
  }
  return rule;
};

// Map a stored rule onto the control state. Legacy fixed-day rules have no exact
// control here, so they show as their nearest interval — editing such a card
// normalizes it, which is fine: the weekday was never the point.
export const ruleToControl = (rule?: string | null): { unit: RecurUnit; n: number } => {
  if (!rule) return { unit: '', n: 1 };
  const m = /^every:(\d{1,3})([dw])(?:@|$)/.exec(rule);
  if (m) return { unit: m[2] as RecurUnit, n: parseInt(m[1], 10) };
  if (rule === 'daily') return { unit: 'd', n: 1 };
  if (rule.startsWith('weekly:')) return { unit: 'w', n: 1 };
  if (rule.startsWith('monthly:')) return { unit: 'd', n: 30 };
  return { unit: '', n: 1 };
};

// The rule string a control state means ('' = 不重複).
export const composeRule = (unit: RecurUnit, n: number): string =>
  unit === '' ? '' : `every:${n}${unit}`;

// Clamp a typed interval into the server-accepted range for its unit.
export const clampInterval = (raw: string | number, unit: RecurUnit): number => {
  const n = typeof raw === 'number' ? raw : parseInt(raw, 10);
  return Math.min(maxForUnit(unit) || 365, Math.max(1, Number.isNaN(n) ? 1 : n));
};

// Whole days between occurrences, or null when the rule isn't an interval.
export const intervalDays = (rule?: string | null): number | null => {
  if (!rule) return null;
  if (rule === 'daily') return 1;
  if (rule.startsWith('weekly:')) return 7;
  if (rule.startsWith('monthly:')) return 30;
  const m = /^every:(\d{1,3})([dw])(?:@|$)/.exec(rule);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return m[2] === 'w' ? n * 7 : n;
};

const atMidnight = (d: Date): Date => {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
};

// The next time a recurring card comes due: the anchor advanced by whole
// intervals until it is today or later. Pure — nothing is written, so the
// reminder is always correct no matter how long nobody looked at the board.
// Returns the plain due date for non-recurring cards, null when there is none.
export const nextOccurrence = (
  dueDate: string | null | undefined,
  rule?: string | null,
  now: Date = new Date()
): Date | null => {
  if (!dueDate) return null;
  const anchor = new Date(dueDate);
  if (isNaN(anchor.getTime())) return null;

  const step = intervalDays(rule);
  if (!step) return anchor;

  const a = atMidnight(anchor);
  const today = atMidnight(now);
  if (a.getTime() >= today.getTime()) return a;

  // Whole periods elapsed since the anchor, rounded up to land on/after today.
  const elapsedDays = Math.round((today.getTime() - a.getTime()) / 86400000);
  const periods = Math.ceil(elapsedDays / step);
  const next = new Date(a);
  next.setDate(next.getDate() + periods * step);
  return next;
};

// Days until the next occurrence (0 = today, negative should not happen).
export const daysUntil = (target: Date | null, now: Date = new Date()): number | null => {
  if (!target) return null;
  return Math.round((atMidnight(target).getTime() - atMidnight(now).getTime()) / 86400000);
};
